import React from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { Button } from "../button";
import { cn } from "../../../utils";

type ModalProps = {
  show: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "lg" | "xl";
  centered?: boolean;
  backdrop?: boolean | "static";
  /**
   * When true, the modal will ask for confirmation before closing
   * (escape, outside click, or close button). Optionally provide a message.
   */
  confirmOnClose?: boolean;
  confirmCloseMessage?: string;
  /** Back-compat: predicate to decide if confirmation is needed. */
  shouldConfirmClose?: boolean | (() => boolean);
};

const sizeClassNames: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "sm:max-w-sm",
  lg: "sm:max-w-3xl",
  xl: "sm:w-[70vw] sm:max-w-[70vw]",
};

export const GenericModal: React.FC<ModalProps> = ({
  show,
  onClose,
  title = "Default Title",
  body = "Default Body",
  footer,
  size = "xl",
  centered = true,
  backdrop = true,
  confirmOnClose = false,
  confirmCloseMessage = "You have unsaved changes. Close anyway?",
  shouldConfirmClose = false,
}) => {
  const skipNextConfirmRef = React.useRef(false);

  const shouldAskConfirmation = React.useCallback(() => {
    if (confirmOnClose) return true;
    return typeof shouldConfirmClose === "function" ? shouldConfirmClose() : Boolean(shouldConfirmClose);
  }, [confirmOnClose, shouldConfirmClose]);

  const confirmClose = React.useCallback(() => {
    if (!shouldAskConfirmation()) {
      return true;
    }
    return window.confirm(confirmCloseMessage);
  }, [confirmCloseMessage, shouldAskConfirmation]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        if (skipNextConfirmRef.current || confirmClose()) {
          skipNextConfirmRef.current = false;
          onClose();
        }
        return;
      }
    },
    [confirmClose, onClose]
  );
  const preventDismiss = backdrop === "static";

  const handleDismissEvent = React.useCallback(
    (event: Event | React.SyntheticEvent) => {
      if (preventDismiss) {
        event.preventDefault();
        return;
      }
      if (shouldAskConfirmation()) {
        if (!confirmClose()) {
          event.preventDefault();
          return;
        }
        // allow close without double-confirm on onOpenChange
        skipNextConfirmRef.current = true;
      }
    },
    [confirmClose, preventDismiss, shouldAskConfirmation]
  );

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(sizeClassNames[size])}
        onEscapeKeyDown={(event) => handleDismissEvent(event)}
        onPointerDownOutside={(event) => handleDismissEvent(event)}
      >
        <DialogHeader className={!centered ? "text-left sm:text-left" : undefined}>
          <DialogTitle>{title}</DialogTitle>
          {typeof body === "string" ? (
            <DialogDescription>{body}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{title}</DialogDescription>
          )}
        </DialogHeader>
        {typeof body !== "string" ? body : null}
        {footer ? (
          <DialogFooter>{footer}</DialogFooter>
        ) : (
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
